import { useState } from 'react';
import { Button } from '@react-apps/design-system';
import type {
  BillingAgreement,
  Offer,
} from '../../domain/entities/sales.entity';
import {
  useBillingAgreement,
  useContractOfferAndArrangements,
} from '../hooks/use-contract-details';
import {
  CancelBillingAgreementModal,
  ResolveCycleCountModal,
  SyncChargeHistoryModal,
} from './sales-action-modals';
import { RenegotiationModal } from './RenegotiationModal';
import { OfferCard } from './OfferCard';
import { BuyerContactCard } from './BuyerContactCard';
import { EntityLink } from '@/shared/components/entity-link';
import {
  BillingAgreementStatusBadge,
  ContractTotalValue,
  Field,
  GatewayBadge,
  Mono,
  ProviderMetadataBlock,
  SectionCard,
  billingAgreementModeLabel,
  chargeIntervalLabel,
  formatCurrency,
  formatDateTime,
} from '../shared/display';

/**
 * Status em que a BA ainda pode ser cancelada (pela API ou manualmente).
 * `not_converted` fica fora de propósito: placeholder terminal, nada a cancelar
 * no gateway.
 */
const BA_CANCELABLE_STATUSES = new Set([
  'waiting_payment',
  'processing',
  'trial',
  'inactive',
  'active',
  'pastdue',
]);

/** Status de DA que já consomem a renegociação daquela BA — não oferecer outra. */
const DA_CHAINED_ACTIVE = ['waiting_payment', 'active', 'completed', 'broken'];

/**
 * Chaves do `providerMetadata` que já têm campo próprio no card — não repetir no
 * bloco genérico. `subscriberCode` entra aqui sem campo dedicado porque na
 * Hotmart ele **é** o `externalId`; repeti-lo só faria o operador procurar a
 * diferença entre dois campos iguais.
 */
const PROMOTED_META_KEYS = ['subscriptionCode', 'subscriberCode'];

function readMetaString(
  metadata: Record<string, unknown> | null,
  key: string,
): string | null {
  const value = metadata?.[key];
  return typeof value === 'string' ? value : null;
}

/**
 * Card de uma Billing Agreement com suas ações (Cancelar / Renegociar) e o bloco
 * de cancelamento. Compartilhado pela aba "Billing Agreement" do contrato (que
 * mostra a BA **atual**) e pela página `/billing-agreements/:id` (que pode
 * mostrar uma BA antiga) — as duas exibem a mesma BA sob as mesmas regras, então
 * duplicar o card significaria manter dois lugares em sincronia.
 */
export function BillingAgreementPanel({
  billingAgreement,
  offer,
  showContract = false,
  linkId = true,
  invalidateKey,
  extraAction,
}: {
  billingAgreement: BillingAgreement;
  /** Oferta da BA — exibida junto porque é o que diz *o que* foi vendido. */
  offer: Offer | null;
  /**
   * Mostra o contrato dono da BA. Só faz sentido em `/billing-agreements/:id`;
   * na aba do contrato seria um link para a própria página.
   */
  showContract?: boolean;
  /** O id leva à página da BA. Desligado na própria página, onde seria um link para si. */
  linkId?: boolean;
  invalidateKey: readonly unknown[];
  /** Botão adicional no header do card (ex.: "Ver acordos de pagamento..."). */
  extraAction?: React.ReactNode;
}) {
  const ba = billingAgreement;
  const [cancelOpen, setCancelOpen] = useState(false);
  const [renegOpen, setRenegOpen] = useState(false);
  const [resolveCyclesOpen, setResolveCyclesOpen] = useState(false);
  const [syncChargesOpen, setSyncChargesOpen] = useState(false);

  /** Só faz sentido no estado que a resolução existe para desempatar. */
  const canResolveCycles = ba.mode === 'subscription' && ba.cycleCount === null;

  const metadata = ba.providerMetadata ?? null;
  // Guru: o id que a URL do painel usa (`/admin/subscriptions/sub_…`). Só
  // existe em assinatura nascida de webhook — BA manual e as anteriores ao
  // parser que o grava vêm sem, e aí o campo não aparece.
  const subscriptionCode = readMetaString(metadata, 'subscriptionCode');
  const extraMeta = Object.entries(metadata ?? {}).filter(
    ([key]) => !PROMOTED_META_KEYS.includes(key),
  );

  const { data: chain, isLoading: chainLoading } =
    useContractOfferAndArrangements(ba.contractId);
  // Invoices da BA **sem filtro de status**: cancelar uma BA marca as `pending`
  // dela como `cancelled`, e são justamente essas que a renegociação precisa
  // oferecer como fonte da dívida. A lista do contrato já vem filtrada e não
  // serve aqui.
  const { data: baDetail, isLoading: invoicesLoading } = useBillingAgreement(
    ba.id,
  );

  const canCancel = BA_CANCELABLE_STATUSES.has(ba.status);
  // Vem do mesmo `useBillingAgreement` já carregado acima — o total de verdade é
  // somado das faturas, não lido de `ba.totalPrice`.
  const valueSummary = baDetail?.valueSummary;
  // Qual gateway gerencia a BA. Vem do mesmo detalhe já carregado — a BA em si
  // só carrega o `providerAccountId`, e a rota que traduz id → conta é restrita
  // aos curingas administrativos (o retorno dela inclui o segredo do webhook).
  const providerAccount = baDetail?.providerAccount;
  // Botão sempre ligado convidaria a clicar em toda BA para descobrir que não
  // havia nada a fazer — quem decide é o sinal que o servidor calcula das nossas
  // faturas e transações: ciclo cobrado faltando, ou transação com identidade
  // inventada que ainda vale procurar no gateway. Some sozinho quando a
  // sincronização resolve os dois.
  const needsChargeHistorySync = baDetail?.needsChargeHistorySync ?? false;

  const dasForBa =
    chain?.debtArrangements.filter(
      (d) => d.replacedBillingAgreement?.id === ba.id,
    ) ?? [];
  const hasDraft = dasForBa.some((d) => d.status === 'draft');
  const alreadyConcluded = dasForBa.some((d) =>
    DA_CHAINED_ACTIVE.includes(d.status),
  );
  const showReneg = ba.status === 'cancelled' && !alreadyConcluded;

  const primaryAction = canCancel ? (
    <Button variant="danger" onClick={() => setCancelOpen(true)}>
      Cancelar BA
    </Button>
  ) : showReneg ? (
    <Button variant="secondary" onClick={() => setRenegOpen(true)}>
      {hasDraft ? 'Continuar renegociação' : 'Criar renegociação'}
    </Button>
  ) : null;

  return (
    <>
      <SectionCard
        title={
          <>
            Billing Agreement
            {/* O gateway é quem cobra e, quando `canCancel` é falso, o único
                que cancela — vale no título, não perdido entre os campos. */}
            {providerAccount && (
              <GatewayBadge provider={providerAccount.provider} />
            )}
          </>
        }
        action={
          extraAction || primaryAction || needsChargeHistorySync ? (
            <div className="flex flex-wrap items-center gap-2">
              {needsChargeHistorySync && (
                <Button
                  variant="secondary"
                  onClick={() => setSyncChargesOpen(true)}
                >
                  Sincronizar cobranças
                </Button>
              )}
              {extraAction}
              {primaryAction}
            </div>
          ) : undefined
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label="ID"
            value={
              <Mono>
                {linkId ? (
                  <EntityLink kind="billingAgreement" id={ba.id} />
                ) : (
                  ba.id
                )}
              </Mono>
            }
          />
          <Field
            label="Status"
            value={<BillingAgreementStatusBadge status={ba.status} />}
          />
          <Field label="Modo" value={billingAgreementModeLabel(ba.mode)} />
          <Field label="Origem" value={ba.origin} />
          {/* Um mesmo gateway pode ter mais de uma conta conectada — o badge do
              título diz qual gateway, este campo diz qual conta. */}
          <Field
            label="Conta do gateway"
            value={providerAccount?.displayName}
          />
          {/* `ba.totalPrice` NÃO é exibido: vindo de webhook ele guarda o valor
              de uma cobrança só (Hotmart: uma parcela; Guru: um ciclo), e só é
              total de vida quando a BA nasce de renegociação. O total honesto é
              somado das faturas, no `valueSummary`. */}
          {ba.mode === 'one_time' ? (
            <Field
              label="Valor"
              value={formatCurrency(valueSummary?.totalValue)}
            />
          ) : (
            <>
              <Field
                label="Valor por ciclo"
                value={formatCurrency(valueSummary?.perCycleValue)}
              />
              <Field
                label="Valor total das faturas"
                value={
                  valueSummary ? (
                    <ContractTotalValue summary={valueSummary} />
                  ) : null
                }
              />
            </>
          )}
          <Field
            label="Ciclos"
            value={
              // `subscription` + `cycleCount: null` = assinatura cujo total de
              // ciclos o webhook não entrega; a ação fica **no próprio campo**
              // que ela resolve, e desaparece assim que ele tem valor.
              canResolveCycles ? (
                <div className="flex items-center gap-2">
                  <span className="text-(--color-text-secondary)">---</span>
                  <Button
                    variant="secondary"
                    size="xs"
                    onClick={() => setResolveCyclesOpen(true)}
                  >
                    Resolver via gateway
                  </Button>
                </div>
              ) : ba.cycleCount !== null ? (
                String(ba.cycleCount)
              ) : null
            }
          />
          <Field
            label="Intervalo de cobrança"
            value={chargeIntervalLabel(
              ba.chargeInterval,
              ba.chargeIntervalUnit,
            )}
          />
          <Field
            label="Pode cancelar via API"
            value={ba.canCancel ? 'Sim' : 'Não'}
          />
          <Field label="ID externo" value={<Mono>{ba.externalId}</Mono>} />
          {/* O `ID externo` acima é o `internal_id` (UUID) e não abre tela
              nenhuma do Guru; quem o operador cola na busca do painel é este. */}
          {subscriptionCode && (
            <Field
              label="Código da assinatura (gateway)"
              value={<Mono>{subscriptionCode}</Mono>}
            />
          )}
          {/* A BA substituída é o caso que mais pede o link: é justamente a que
              some da aba do contrato depois da renegociação. */}
          <Field
            label="Substitui a BA"
            value={
              ba.replacesBillingAgreementId ? (
                <Mono>
                  <EntityLink
                    kind="billingAgreement"
                    id={ba.replacesBillingAgreementId}
                  />
                </Mono>
              ) : null
            }
          />
          {showContract && (
            <Field
              label="Contrato"
              value={
                <Mono>
                  <EntityLink kind="contract" id={ba.contractId} />
                </Mono>
              }
            />
          )}
          <Field
            label="Último status em"
            value={formatDateTime(ba.lastStatusAt)}
          />
          <Field label="Atualizada em" value={formatDateTime(ba.updatedAt)} />
        </div>

        <ProviderMetadataBlock entries={extraMeta} />
      </SectionCard>

      {(ba.cancelledAt ||
        ba.cancelReason ||
        ba.cancelledByName ||
        ba.cancelledByEmail) && (
        <SectionCard title="Cancelamento">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              label="Cancelada em"
              value={formatDateTime(ba.cancelledAt)}
            />
            <Field label="Motivo" value={ba.cancelReason} />
            <Field label="Por (nome)" value={ba.cancelledByName} />
            <Field label="Por (e-mail)" value={ba.cancelledByEmail} />
          </div>
        </SectionCard>
      )}

      <BuyerContactCard billingAgreement={ba} />

      <OfferCard offer={offer} />

      {showReneg && (
        <RenegotiationModal
          invalidateKey={invalidateKey}
          billingAgreement={ba}
          chain={chain}
          chainLoading={chainLoading}
          invoicesData={baDetail}
          invoicesLoading={invoicesLoading}
          open={renegOpen}
          onClose={() => setRenegOpen(false)}
        />
      )}

      {needsChargeHistorySync && (
        <SyncChargeHistoryModal
          invalidateKey={invalidateKey}
          target={{ kind: 'billingAgreement', billingAgreementId: ba.id }}
          open={syncChargesOpen}
          onClose={() => setSyncChargesOpen(false)}
        />
      )}

      {canResolveCycles && (
        <ResolveCycleCountModal
          invalidateKey={invalidateKey}
          billingAgreement={ba}
          open={resolveCyclesOpen}
          onClose={() => setResolveCyclesOpen(false)}
        />
      )}

      <CancelBillingAgreementModal
        title="Cancelar Billing Agreement"
        invalidateKey={invalidateKey}
        billingAgreement={ba}
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
      />
    </>
  );
}
